import type { Partner } from "@expert/shared-types";

import type { EvaluationDetailsBody } from "../api/evaluations/types";
import type { ChecklistCategory } from "./consts";

import {
    CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_ATT,
    CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_VERIZON,
    CHECKLIST_CATEGORIES,
} from "./consts";

/**
 * The updated call flow checklists are the standard for att and verizon; every other partner
 * still uses the original checklist.
 */
export function getChecklistCategories(partner: Partner): ChecklistCategory[] {
    switch (partner) {
        case "att":
            return CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_ATT;
        case "verizon":
            return CALL_FLOW_UPDATED_CHECKLIST_CATEGORIES_VERIZON;
        default:
            return CHECKLIST_CATEGORIES;
    }
}

export function getChecklistCategoryCount({
    category,
    behavior,
    totalBehaviors,
    partner,
}: {
    category: string;
    behavior: string;
    totalBehaviors: number;
    partner: Partner;
}): { currentIndex: number; total: number } {
    const checklistCategories = getChecklistCategories(partner);
    const categoryItem = checklistCategories.find((cat) => cat.category === category);
    if (!categoryItem) {
        return { currentIndex: 0, total: 0 };
    }

    // If there are no sale detected behaviors, return the length of just the behaviors array
    if (categoryItem.saleDetectedBehaviors.length === 0) {
        return {
            currentIndex: categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1,
            total: categoryItem.behaviors.length,
        };
    }

    const sumOfAvailableBehaviors = checklistCategories.reduce((acc, cur) => {
        return acc + (cur.behaviors.length + cur.saleDetectedBehaviors.length);
    }, 0);
    const salesBehaviorDifference = sumOfAvailableBehaviors - totalBehaviors;
    const total = categoryItem.behaviors.length + categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference;

    // Check if the behavior is in the main behaviors array
    if (categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) >= 0) {
        return {
            currentIndex: categoryItem.behaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1,
            total,
        };
    }

    // If the behavior is not in the main behaviors array, it must be in the sale detected behaviors array
    // and its index needs to be calculated accordingly
    let salesBehaviorIndex = categoryItem.saleDetectedBehaviors.findIndex((b) => b.evaluationBehavior === behavior) + 1;
    if (salesBehaviorIndex >= categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference) {
        salesBehaviorIndex = categoryItem.saleDetectedBehaviors.length - salesBehaviorDifference;
    }
    return {
        currentIndex: categoryItem.behaviors.length + salesBehaviorIndex,
        total,
    };
}

export function sortBehaviorsByChecklist(evaluationResultsBehaviors: EvaluationDetailsBody[], partner: Partner) {
    const orderedBehaviors: (EvaluationDetailsBody & { category: string })[] = [];
    getChecklistCategories(partner).forEach((category) => {
        /**
         * Loop through each behavior in the category and
         * match it to the behaviors from evaluationResults
         */
        category.behaviors.forEach((behavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? behavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });

        category.saleDetectedBehaviors.forEach((salesBehavior) => {
            const behaviorItem = evaluationResultsBehaviors.find((b) =>
                b.behavior
                    ? salesBehavior.evaluationBehavior.toLocaleLowerCase().includes(b.behavior.toLocaleLowerCase())
                    : false,
            );

            if (behaviorItem) {
                orderedBehaviors.push({
                    ...behaviorItem,
                    category: category.category,
                });
            }
        });
    });

    return orderedBehaviors;
}
